import { SimpleTableCell } from "../../../common/components/functional/simpleTable/cellHelpers";
import { Payslip } from "./payslipAPI";
import { ExportPayslipPDF } from "./payslipPDF/ExportPayslipPDF";

export type PayslipDataRow = {
  id: string;
  isManual: boolean;
  fields: SimpleTableCell[];
};

// Widths need to match widths in SalaryPagePayslips.tsx
export const formatSalaryPagePayslipData = (
  payslips: Payslip[],
  openPayslip: (id: string) => void,
  openWageReport: (id: string | null) => void,
  navigate: (path: string) => void,
): PayslipDataRow[] => {
  return payslips.map((item) => {
    return {
      id: item.id,
      isManual: item.isManual,
      fields: [
        {
          name: "salaryDate",
          value: item.salaryDate,
          type: "date",
          width: "90px",
        },
        {
          name: "payer",
          value: item.payer?.name ?? "",
          width: "220px",
        },
        {
          name: "valueDate",
          value: item.valueDate,
          type: "date",
          width: "90px",
        },
        {
          name: "wageReport",
          value: item.incomeRegisterId ?? "",
          type: "link",
          onClick: () => openWageReport(item.incomeRegisterId),
          width: "90px",
        },
        {
          name: "payslipNumber",
          value: item.payslipNumber.toString(),
          type: "link",
          width: "90px",
          onClick: () => openPayslip(item.id),
        },
        {
          name: "recipient",
          value: item.recipient?.name ?? "",
          type: "link",
          width: "200px",
          linkProps: {
            href: `/home/talents/${item.talentId}/employment`,
            target: "_blank",
            rel: "noopener noreferrer",
          },
        },
        {
          name: "ssn",
          value: item.recipient?.ssn ?? "",
          width: "100px",
        },
        {
          name: "payslipPeriodStart",
          value: item.periodStart,
          type: "date",
          width: "90px",
        },
        {
          name: "payslipPeriodEnd",
          value: item.periodEnd,
          type: "date",
          width: "90px",
        },
        {
          name: "fringeBenefits",
          value: item.subtotalsPeriod.fringeBenefits,
          type: "number",
          width: "90px",
        },
        {
          name: "healthInsurance",
          value: item.subtotalsPeriod.employerHealthInsurance,
          type: "number",
          width: "90px",
        },
        {
          name: "taxable",
          value: item.subtotalsPeriod.taxable,
          type: "number",
          width: "90px",
        },
        {
          name: "incomeGross",
          value: item.subtotalsPeriod.incomeGross,
          type: "number",
          width: "90px",
        },
        {
          name: "taxWithholding",
          value: item.subtotalsPeriod.taxWithholding,
          type: "number",
          width: "90px",
        },
        {
          name: "pensionInsurance",
          value: item.subtotalsPeriod.pensionInsurance,
          type: "number",
          width: "90px",
        },
        {
          name: "unemploymentInsurance",
          value: item.subtotalsPeriod.unemploymentInsurance,
          type: "number",
          width: "90px",
        },
        {
          name: "taxExempt",
          value: item.subtotalsPeriod.taxExempt,
          type: "number",
          width: "90px",
        },
        {
          name: "totalPayment",
          value: item.subtotalsPeriod.incomeNet,
          type: "number",
          width: "90px",
        },
        {
          name: "status",
          value: item.status.code,
          type: "key",
          color:
            item.status.code === "Approved"
              ? "green"
              : item.status.code === "Error"
                ? "red"
                : "black",
        },
      ],
    };
  });
};

// Widths need to match widths in PayslipsOverview.tsx
export const formatPayslipOverviewData = (
  payslips: Payslip[],
  openPayslip: (id: string) => void,
): PayslipDataRow[] => {
  return payslips.map((item) => {
    return {
      id: item.id,
      isManual: item.isManual,
      fields: [
        {
          name: "salaryDate",
          value: item.salaryDate,
          type: "date",
          width: "90px",
        },
        { name: "payer", value: item.payer?.name, width: "180px" },
        {
          name: "valueDate",
          value: item.valueDate,
          type: "date",
          width: "90px",
        },
        {
          name: "payslipNumber",
          value: item.payslipNumber.toString(),
          type: "link",
          width: "70px",
          onClick: () => {
            openPayslip(item.id);
          },
          component: <ExportPayslipPDF payslipId={item.id} />,
        },
        {
          name: "payslipPeriodStart",
          value: item.periodStart,
          type: "date",
          width: "90px",
        },
        {
          name: "payslipPeriodEnd",
          value: item.periodEnd,
          type: "date",
          width: "90px",
        },
        {
          name: "taxable",
          value: item.subtotalsPeriod.taxable,
          type: "number",
          width: "80px",
        },
        {
          name: "incomeGross",
          value: item.subtotalsPeriod.incomeGross,
          type: "number",
          width: "80px",
        },
        {
          name: "taxWithholding",
          value: item.subtotalsPeriod.taxWithholding,
          type: "number",
          width: "90px",
        },
        {
          name: "pensionInsurance",
          value: item.subtotalsPeriod.pensionInsurance,
          type: "number",
          width: "80px",
        },
        {
          name: "unemploymentInsurance",
          value: item.subtotalsPeriod.unemploymentInsurance,
          type: "number",
          width: "80px",
        },
        {
          name: "taxExempt",
          value: item.subtotalsPeriod.taxExempt,
          type: "number",
          width: "80px",
        },
        {
          name: "totalPayment",
          value: item.subtotalsPeriod.incomeNet,
          type: "number",
          width: "80px",
        },
        {
          name: "status",
          value: item.status.code,
          type: "key",
          width: "150px",
        },
      ],
    };
  });
};
